/**
 * @license observable-hooks
 * index.ts
 *
 * Copyright (c) 2023 CRIMX<straybugs@gmail.com>
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 */
import { type MutableRefObject, useEffect, useRef } from 'react';

import type { Observable } from 'rxjs/Observable';
import type { Subscription } from 'rxjs/Subscription';

import type { PartialObserver } from '../rx-fallbacks';

import { useIsomorphicLayoutEffect } from './useIsomorphicLayoutEffect';

interface Observer<T> {
	next?: (value: T) => void;
	error?: (err: any) => void;
	complete?: () => void;
}

type Args<TInput> = [
	Observable<TInput>, // inputs$
	PartialObserver<TInput> | ((value: TInput) => void) | null | undefined,
	((error: any) => void) | null | undefined,
	(() => void) | null | undefined,
];

const toObserver = <T>(args: Args<T>): Observer<T> =>
	(args[1] as PartialObserver<T>)?.next
		? (args[1] as Observer<T>)
		: {
				next: args[1] as Observer<T>['next'],
				error: args[2] as Observer<T>['error'],
				complete: args[3] as Observer<T>['complete'],
			};

/**
 *
 * @template TInput Input value within Observable.
 *
 * @param useCustomEffect useEffect or useLayoutEffect
 * @param args collected arguments
 */
function useSubscriptionInternal<TInput>(
	args: Args<TInput>,
): MutableRefObject<Subscription | undefined> {
	const argsRef = useRef(args);
	const observerRef = useRef<Observer<TInput>>();
	const subscriptionRef = useRef<Subscription>();

	const deps = args[0];

	// Update the latest observable and callbacks
	// synchronously after render being committed
	useIsomorphicLayoutEffect(() => {
		argsRef.current = args;
		observerRef.current = toObserver(args);
	});

	useEffect(() => {
		// keep in closure for checking staleness
		const input$ = argsRef.current[0];

		/* istanbul ignore if: Just in case the layoutEffect order is agnostic */
		if (!observerRef.current) {
			observerRef.current = toObserver(argsRef.current);
		}

		const subscription = input$.subscribe({
			next: (value) => {
				if (input$ === argsRef.current[0]) {
					observerRef.current!.next?.(value);
				}
				// else: stale observable
			},
			error: (error) => {
				if (input$ === argsRef.current[0]) {
					observerRef.current!.error
						? observerRef.current!.error(error)
						: // eslint-disable-next-line no-console
							console.error(error);
				}
				// else: stale observable
			},
			complete: () => {
				if (input$ === argsRef.current[0]) {
					observerRef.current!.complete?.();
				}
				// else: stale observable
			},
		});

		subscriptionRef.current = subscription;

		return () => {
			subscription.unsubscribe();
		};
	}, [deps]);

	return subscriptionRef;
}

/**
 * Accepts an Observable and optional `next`, `error`, `complete` functions.
 * These functions must be in correct order.
 * Use `undefined` or `null` for placeholder.
 *
 * Subscription will unsubscribe when unmount, you can also
 * unsubscribe manually.
 *
 * ⚠ **Note:** To make it concurrent mode compatible, the subscription happens
 * after the render is committed to the screen
 * which means even the Observable emits synchronous values
 * they will arrive after the first rendering.
 *
 * Note that changes of callbacks will not trigger
 * an emission. If you need that just create another
 * Observable of the callback with [[useObservable]].
 *
 * (From v2.0) You can access closure directly inside callback like in `useEffect`.
 * `useSubscription` will ensure the latest callback is called.
 *
 * (From v2.3.4) when the Observable changes `useSubscription` will automatically
 * unsubscribe the old one and resubscribe to the new one.
 *
 * ⚠ **Note:** Due to the design of RxJS, once an error occurs in an observable, the observable
 * is killed.
 * You should prevent errors from reaching observables or `catchError` in sub-observables.
 * You can also make the observable as state and replace it on error.
 * `useSubscription` will automatically switch to the new one.
 *
 * @template TInput Input value within Observable.
 *
 * @param input$ Input Observable.
 * @param observer Observer
 */
export function useSubscription<TInput>(
	input$: Observable<TInput>,
	observer?: PartialObserver<TInput>,
): MutableRefObject<Subscription | undefined>;
/**
 * @template TInput Input value within Observable.
 *
 * @param input$ Input Observable.
 * @param next Notify when a new value is emitted.
 * @param error Notify when a new error is thrown.
 * @param complete Notify when the Observable is complete.
 */
export function useSubscription<TInput>(
	input$: Observable<TInput>,
	next?: ((value: TInput) => void) | null | undefined,
	error?: ((error: any) => void) | null | undefined,
	complete?: (() => void) | null | undefined,
): MutableRefObject<Subscription | undefined>;
export function useSubscription<TInput>(
	input$: Observable<TInput>,
	observerOrNext$?: PartialObserver<TInput> | ((value: TInput) => void) | null | undefined,
	error?: ((error: any) => void) | null | undefined,
	complete?: (() => void) | null | undefined,
): MutableRefObject<Subscription | undefined> {
	return useSubscriptionInternal([input$, observerOrNext$, error, complete]);
}
